import * as trace from 'owa-trace';
import createCalendarEventOperation from 'owa-service/lib/operation/createCalendarEventOperation';
import createItemRequest from 'owa-service/lib/factory/createItemRequest';
import folderId from 'owa-service/lib/factory/folderId';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import itemResponseShape from 'owa-service/lib/factory/itemResponseShape';
import targetFolderId from 'owa-service/lib/factory/targetFolderId';
import { CalendarEvent } from 'mini-calendar-event-types';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { getUserTimeZone } from 'owa-session-store';
import { toCalendarItem } from 'mini-calendar-event-converter';
import getMailboxRequestOptions from 'owa-service-utils/lib/getMailboxRequestOptions';

/**
 * Calls createCalendarEvent to create given event
 */
export default async function createCalendarEventService(
    event: CalendarEvent
): Promise<ItemInfoResponseMessage> {
    try {
        const responseShape = itemResponseShape({
            BaseShape: 'IdOnly',
        });

        // We need to set the charm to undefined because the Create call will fail if specified.
        const serviceItem = toCalendarItem(event);
        // We have a bug in the server where passing a InstanceKey GUID was breaking.
        // While utah change is fixed/reverted, removing instanceKey from the create request so that REACT client is unblocked
        serviceItem.InstanceKey = undefined;

        const itemFolderId = targetFolderId({
            BaseFolderId: folderId({
                Id: event.ParentFolderId.Id,
            }),
        });

        // As this is a new calendar event so no ItemId will be there hence we use the parent folder id mailbox info
        const request = createItemRequest({
            Items: [serviceItem],
            SavedItemFolderId: itemFolderId,
            ClientSupportsIrm: true,
            OutboundCharset: 'AutoDetect',
            UnpromotedInlineImageCount: 0,
            ItemShape: responseShape,
            SendMeetingInvitations: 'SendToNone',
        });

        // There is some code in client-web related to skip sending meeting invite to group.
        // Have removed it till we understand it better.

        // The server seems unable (or unwilling) to respect recurrence start/end
        // dates that are sent with time zone offsets (UTC or others). So the only
        // way I see for recurrence to be created properly when other time zones
        // are selected is for us to send the recurrence range as plain dates and
        // for the request to go out with the event's start time zone in the header.
        // This is how jsMVVM did it, so we'll replicate it here.
        /**
         * FOR now let us honour the user setting only to fix Item https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/51164
         */
        const Header = getJsonRequestHeader();
        Header.TimeZoneContext.TimeZoneDefinition.Id = event.StartTimeZoneId || getUserTimeZone();

        const response = await createCalendarEventOperation(
            {
                Header,
                Body: request,
            },
            getMailboxRequestOptions(event.ParentFolderId.mailboxInfo)
        );

        return response.Body.ResponseMessages.Items[0] as ItemInfoResponseMessage;
    } catch (error) {
        trace.errorThatWillCauseAlert(
            'Calendar create event service request failed: ' + error.Message
        );
        return null;
    }
}
